import { HttpClient } from '@angular/common/http'
import { Injectable } from '@angular/core'
import {
  EventType,
  HeliosMessageType,
  LoadEvent,
  SessionMessagingService,
} from '@helios/messaging'
import {
  HeliosSessionLoadError,
  reportError,
  resetSessionCacheStatus,
  RootState,
  selectActiveSession,
  SessionCacheName,
  SessionCacheStatus,
  setSessionCacheStatus,
} from '@helios/state-management'
import { getToday } from '@helios/utils'
import { Store } from '@ngrx/store'
import { Observable, Subject, timer } from 'rxjs'
import {
  distinctUntilChanged,
  map,
  switchMap,
  takeUntil,
  takeWhile,
  tap,
} from 'rxjs/operators'
import { ErrorInterceptor } from '../interceptors/error.interceptor'
import { v4 as UUID } from 'uuid'

/**
 * debugging, because this can get tricksy. turn to false before merge!
 */
function log(msg: string) {
  // eslint-disable-next-line no-console
  console.log(`[CACHEDEBUG] ${msg}`)
}
const DEBUG = false

/**
 * The `sessionLoadingStatus` payload. Every key is optional because which caches a session has
 * depends on the session kind: a CW session never reports the four `flex-*` keys, and an ENFN
 * session never reports the three legacy ones. Nothing may assume a fixed set of keys — see
 * {@link SessionCacheStatusService.allComplete}.
 */
interface SessionStatusResponse {
  'clearwater-transactions'?: SessionCacheStatus
  'external-transactions'?: SessionCacheStatus
  positions?: SessionCacheStatus
  'flex-positions'?: SessionCacheStatus
  'flex-external-positions'?: SessionCacheStatus
  'flex-transactions'?: SessionCacheStatus
  'flex-external-transactions'?: SessionCacheStatus
}

/**
 * Which redux slot each response key feeds. Doubles as the enumeration of every key we recognise,
 * so a key the backend adds later is ignored rather than mistaken for a cache we gate on.
 */
const RESPONSE_KEY_TO_CACHE: Record<
  keyof SessionStatusResponse,
  SessionCacheName
> = {
  'clearwater-transactions': SessionCacheName.CwTransactions,
  'external-transactions': SessionCacheName.ExtTransactions,
  positions: SessionCacheName.Positions,
  'flex-positions': SessionCacheName.FlexPositions,
  'flex-external-positions': SessionCacheName.FlexExternalPositions,
  'flex-transactions': SessionCacheName.FlexTransactions,
  'flex-external-transactions': SessionCacheName.FlexExternalTransactions,
}

const RESPONSE_KEYS = Object.keys(
  RESPONSE_KEY_TO_CACHE,
) as (keyof SessionStatusResponse)[]

/**
 * Monitors SSE to update session cache status in redux.
 * Also polls http for session status.
 * Resets status when a new session is started.
 */
@Injectable({
  providedIn: 'root',
})
export class SessionCacheStatusService {
  BASE_URL = `helios-sessions`
  SESSIONS_STATUS_URL = `${this.BASE_URL}/sessionLoadingStatus`
  HTTP_POLL_INTERVAL = 5 * 1000 //5 sec

  private readonly sessionChanged$ = new Subject<void>()
  private readonly _destroyed$ = new Subject<void>()

  //exposed for test
  currentSessionId?: string

  constructor(
    private readonly store: Store<RootState>,
    private readonly messagingService: SessionMessagingService,
    private readonly http: HttpClient,
  ) {}

  initialize() {
    //newSessionId tracks when session changes - from undefined to an id, or changed ids, or back to undefined.
    this.store
      .select(selectActiveSession)
      .pipe(
        takeUntil(this._destroyed$),
        map(s => s?.id),
        distinctUntilChanged((id1, id2) => id1 === id2),
      )
      .subscribe(sessionId => this.handleSessionChange(sessionId))

    //constantly monitor SSE load events, we don't really ever disconnect
    this.monitorMessages(
      SessionCacheName.CwTransactions,
      HeliosMessageType.CWTransactionLoad,
    )
    this.monitorMessages(
      SessionCacheName.ExtTransactions,
      HeliosMessageType.ExternalTransactionLoad,
    )
    this.monitorMessages(
      SessionCacheName.Positions,
      HeliosMessageType.PositionLoad,
    )
    // Flex (2.0) caches. Independent of the three above: a session emits one family or the other,
    // never both, which is why the polling stop-check below cannot assume a fixed key set.
    this.monitorMessages(
      SessionCacheName.FlexPositions,
      HeliosMessageType.FlexPositionLoad,
    )
    this.monitorMessages(
      SessionCacheName.FlexExternalPositions,
      HeliosMessageType.FlexExternalPositionLoad,
    )
    this.monitorMessages(
      SessionCacheName.FlexTransactions,
      HeliosMessageType.FlexTransactionLoad,
    )
    this.monitorMessages(
      SessionCacheName.FlexExternalTransactions,
      HeliosMessageType.FlexExternalTransactionLoad,
    )
  }

  cleanup() {
    this._destroyed$.next()
    this._destroyed$.complete()
  }

  private handleSessionChange(sessionId: string | undefined) {
    if (DEBUG) log(`new sessionId: ${sessionId}`)
    this.currentSessionId = sessionId
    this.store.dispatch(resetSessionCacheStatus())

    this.sessionChanged$.next() //signal any prior http polling to stop
    if (sessionId) this.pollHttpSessionStatus(sessionId)
  }

  /** @internal for testing */
  monitorMessages(
    sessionCacheName: SessionCacheName,
    loadEventType: LoadEvent['eventType'],
  ) {
    this.messagingService
      .on<unknown, LoadEvent>(EventType.MESSAGE, loadEventType)
      .pipe(takeUntil(this._destroyed$))
      .subscribe(event => {
        //IMPORTANT! ignore late or spurious events
        if (event.heliosSessionId != this.currentSessionId) {
          if (DEBUG) log(`SSE CacheStatus: mis-matched sessionId, ignored`)
          return
        }
        const status: SessionCacheStatus = event.payload.status

        if (DEBUG) log(`SSE CacheStatus: ${sessionCacheName}:${status}`)

        if (status === SessionCacheStatus.ERROR) {
          this.handleStandardApiError(event)
        }

        this.updateReduxStatus(sessionCacheName, status)
      })
  }

  private updateReduxStatus(
    sessionCacheName: SessionCacheName,
    sessionCacheStatus: SessionCacheStatus,
  ) {
    this.store.dispatch(
      setSessionCacheStatus({
        name: sessionCacheName,
        status: sessionCacheStatus,
      }),
    )
  }

  /**
   * Poll the http request
   * As of 2022-11-03, some SSE events are missed.  Often, the transaction status is missed, which destabilizes cypress testing
   * Until we have more reliable ui events, we'll just poll every few seconds.
   *
   * Note that even if SSE events are 100% reliable, we may not have been always listening,
   *   because the session could have been started earlier before this app instance was running.
   *   so we will ALWAYS need to start with one initial http call to get the initial status.
   */
  /** @internal for testing */
  pollHttpSessionStatus(sessionId: string) {
    timer(0, this.HTTP_POLL_INTERVAL) // start immediately, then after each interval
      .pipe(
        takeUntil(this.sessionChanged$),
        switchMap(() => this.getSessionStatusHttp(sessionId)),
        tap(response => {
          if (DEBUG) log(`HTTP CacheStatus: ${JSON.stringify(response)}`)
          this.reportedStatuses(response).forEach(([name, status]) =>
            this.updateReduxStatus(name, status),
          )
        }),
        takeWhile(response => !this.allComplete(response)),
      )
      .subscribe()
  }

  /**
   * raw http request to get the status for the eventType for the given session
   */
  private getSessionStatusHttp(
    sessionId: string,
  ): Observable<SessionStatusResponse> {
    return this.http.get<SessionStatusResponse>(
      `${this.SESSIONS_STATUS_URL}/${sessionId}`,
    )
  }

  /**
   * The (slot, status) pairs the response actually carried, in a fixed order.
   *
   * A key the response omits is NOT reported: the session simply has no such cache, and writing an
   * `undefined` into redux would look like a real status to every gating selector.
   *
   * @param response one `sessionLoadingStatus` payload
   * @returns one entry per recognised key present in the payload
   */
  private reportedStatuses(
    response: SessionStatusResponse,
  ): [SessionCacheName, SessionCacheStatus][] {
    return RESPONSE_KEYS.filter(key => response[key] != null).map(key => [
      RESPONSE_KEY_TO_CACHE[key],
      response[key] as SessionCacheStatus,
    ])
  }

  /**
   * Once every cache the session actually has is in a terminal state, we do not need to poll
   * anymore.
   *
   * Only keys present in the response count. A session reports one family of caches or the other
   * (legacy CW vs flex), so a flat "all seven are done" check would never be satisfied and the
   * poller would run for the life of the session. The converse also matters: an empty or wholly
   * unrecognised payload is not "everything finished" — stopping there would strand every panel on
   * a status that never arrives, so polling continues.
   *
   * @param response one `sessionLoadingStatus` payload
   * @returns true when polling can stop
   */
  private allComplete(response: SessionStatusResponse) {
    const reported = this.reportedStatuses(response)
    if (!reported.length) return false
    return reported.every(([, status]) => this.isStatusComplete(status))
  }

  /**
   * DONE and ERROR are the only terminal statuses. POST_LOAD_DONE deliberately stays out of this:
   * it means DONE has already been reported and further post-load work can still report again, so
   * a poller that stopped on it would miss that later report.
   *
   * @param status one reported cache status
   * @returns true when the loader will not report on this cache again
   */
  private isStatusComplete(status: SessionCacheStatus) {
    return (
      status === SessionCacheStatus.DONE || status === SessionCacheStatus.ERROR
    )
  }

  /**
   * @internal
   * @param eventType
   * @param error
   */
  private handleStandardApiError(loadEvent: LoadEvent) {
    const error = loadEvent.payload.errorDetails ?? {}
    const msg = ErrorInterceptor.standardApiErrorToMessage(
      error,
      loadEvent.eventType,
    )

    this.store.dispatch(
      reportError({
        id: UUID(),
        timestamp: getToday(),
        message: msg,
        type: 'session-load',
        sessionId: loadEvent.heliosSessionId,
        error: JSON.stringify(error),
        service: loadEvent.eventType.valueOf(),
        status: loadEvent.payload.status,
      } as HeliosSessionLoadError),
    )
  }
}
